"use client"

import { track } from "@opensea/analytics"
import { graphql, useQuery } from "@opensea/graphql"
import { classNames, TextBody, useToasts } from "@opensea/ui-kit"
import { useCookie, useIsHydrated } from "@opensea/ui-kit/hooks"
import { motion } from "motion/react"
import { useTranslations } from "next-intl"
import { useEffect, useRef, useState } from "react"
import {
  useGetVoyageId,
  useIsVoyageScrollIndicatorEnabled,
} from "@/hooks/use-flags"
import { useAddress, useAuthenticated } from "@/hooks/wallet"

const QuestsQuery = graphql(
  `
    query VoyageScrollIndicatorQuery($address: Address!) {
      userQuests(address: $address) {
        quest {
          id
        }
        isComplete
        parentQuests {
          id
        }
      }
    }
  `,
  [],
)

// Constants
const STROKE_DASHARRAY = 472
const SVG_WIDTH = 200
const SVG_HEIGHT = 52
const BORDER_RADIUS = 24
const SCROLL_THROTTLE_MS = 16 // ~60fps

interface LockIconProps {
  className?: string
  isUnlocked: boolean
}

// TODO: Commented out due to deleted mutations in os2-core PR #23389
// const setQuestProgressMutation = graphql(`
//   mutation setQuestProgress($questId: String!) {
//     setQuestProgress(questId: $questId) {
//       success
//     }
//   }
// `)

const LockIcon: React.FC<LockIconProps> = ({ className, isUnlocked }) => {
  return (
    <motion.svg
      aria-hidden="true"
      className={className}
      fill="none"
      height="24"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 10H16M8 10C6.34315 10 5 11.3431 5 13V18C5 19.6569 6.34315 21 8 21H16C17.6569 21 19 19.6569 19 18V13C19 11.3431 17.6569 10 16 10"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <motion.path
        animate={{
          strokeDashoffset: isUnlocked ? 12 : 0,
        }}
        d="M8 10V7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7V10"
        initial={false}
        strokeDasharray={25}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        transition={{
          type: "spring",
          duration: 0.6,
          bounce: 0.2,
        }}
      />
      <path
        d="M12 14V17"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </motion.svg>
  )
}

interface VoyageScrollIndicatorProps {
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>
  duration?: number
  className?: string
}

const VoyageScrollIndicator = ({
  scrollContainerRef,
  duration = 0.3,
  className,
}: VoyageScrollIndicatorProps) => {
  const [progress, setProgress] = useState(0)
  const throttleTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // TODO: Commented out due to deleted mutations in os2-core PR #23389
  // const setQuestProgress = useMutation(setQuestProgressMutation)[1]
  const t = useTranslations("VoyageScrollIndicator")
  const isAuthenticated = useAuthenticated()
  const { showSuccessMessage } = useToasts()
  const [showVoyageScrollIndicator, setShowVoyageScrollIndicator] = useCookie(
    "showVoyageScrollIndicator-overview",
  )
  const isVoyageScrollIndicatorEnabled = useIsVoyageScrollIndicatorEnabled()
  const isHydrated = useIsHydrated()
  const address = useAddress()
  const [{ data }] = useQuery(
    {
      query: QuestsQuery,
      variables: {
        address: address ?? "",
      },
      pause: !(
        address &&
        isAuthenticated &&
        isVoyageScrollIndicatorEnabled &&
        isHydrated
      ),
    },
    { throwOnError: false },
  )
  const voyageId = useGetVoyageId()

  function getIsParentVoyageComplete() {
    if (!(data?.userQuests && voyageId)) {
      return false
    }

    const currentVoyage = data.userQuests.find(q => q.quest.id === voyageId)
    const parentId = currentVoyage?.parentQuests?.[0]?.id

    if (!parentId) {
      return false
    }

    const parentVoyage = data.userQuests.find(q => q.quest.id === parentId)
    return parentVoyage?.isComplete ?? false
  }

  const isParentVoyageComplete = getIsParentVoyageComplete()

  function calculateScrollProgress() {
    let scrollTop: number
    let scrollHeight: number

    if (scrollContainerRef?.current) {
      const container = scrollContainerRef.current
      scrollTop = container.scrollTop
      scrollHeight = container.scrollHeight - container.clientHeight
    } else {
      scrollTop = window.scrollY
      scrollHeight = document.documentElement.scrollHeight - window.innerHeight
    }

    if (scrollHeight <= 0) {
      return 0
    }
    // Adjust calculation so 100% is reached at 90% scroll instead of 100%
    const scrollPercent = (scrollTop / scrollHeight / 0.9) * 100
    return Math.min(Math.max(scrollPercent, 0), 100)
  }

  function handleScroll() {
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current)
    }

    throttleTimeoutRef.current = setTimeout(() => {
      const newProgress = calculateScrollProgress()
      setProgress(newProgress)
    }, SCROLL_THROTTLE_MS)
  }

  useEffect(() => {
    const container = scrollContainerRef?.current

    if (container) {
      container.addEventListener("scroll", handleScroll, { passive: true })
    } else {
      window.addEventListener("scroll", handleScroll, { passive: true })
    }

    // Calculate initial progress
    const initialProgress = calculateScrollProgress()
    setProgress(initialProgress)

    return () => {
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current)
      }

      if (container) {
        container.removeEventListener("scroll", handleScroll)
      } else {
        window.removeEventListener("scroll", handleScroll)
      }
    }
  }, [scrollContainerRef])

  const strokeProgress = progress >= 100 ? 100 : progress
  const isUnlocked = progress >= 100

  async function handleComplete() {
    if (!isUnlocked) {
      return
    }
    if (!voyageId) {
      return
    }

    void track("Rewards Complete Voyage", {
      voyage_id: voyageId,
      progress,
    })

    // TODO: Commented out due to deleted mutations in os2-core PR #23389 (important-comment)
    // try {
    //   await setQuestProgress({
    //     questId: voyageId,
    //   })
    //   showSuccessMessage(t("completeVoyageSuccess"))
    //   setShowVoyageScrollIndicator("false")
    // } catch {
    //   showErrorMessage(t("completeVoyageError"))
    // }

    // Temporary implementation until mutations are restored (important-comment)
    showSuccessMessage(t("completeVoyageSuccess"))
    setShowVoyageScrollIndicator("false")
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      handleComplete()
    }
  }

  if (
    !isAuthenticated ||
    showVoyageScrollIndicator === "false" ||
    !isVoyageScrollIndicatorEnabled ||
    !isHydrated ||
    !isParentVoyageComplete ||
    !voyageId
  ) {
    return null
  }

  return (
    <motion.button
      animate={{ scale: isUnlocked ? 1.03 : 1 }}
      className={classNames(
        "-translate-x-1/2 fixed bottom-24 left-1/2 z-action-bar flex h-12 w-[196px] items-center justify-center gap-1 rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-3 focus:ring-offset-2 lg:bottom-12",
        isUnlocked ? "bg-blue-3" : "bg-bg-primary",
        className,
      )}
      onClick={handleComplete}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      transition={{
        type: "spring",
        duration: 0.4,
        bounce: 0,
      }}
      whileTap={{ scale: 0.97 }}
    >
      <LockIcon
        className={classNames(
          "size-5 transition-colors duration-200 ease-in-out",
          isUnlocked ? "stroke-white" : "stroke-text-tertiary",
        )}
        isUnlocked={isUnlocked}
      />
      <TextBody
        className={classNames(
          "select-none font-medium transition-colors duration-200 ease-in-out",
          isUnlocked ? "text-white" : "text-text-tertiary",
        )}
        size="md"
      >
        {t("completeVoyage")}
      </TextBody>
      <svg
        aria-hidden="true"
        className="-left-0.5 -top-0.5 absolute inset-0"
        fill="none"
        height={SVG_HEIGHT}
        style={{ willChange: "transform" }}
        viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
        width={SVG_WIDTH}
      >
        <motion.path
          animate={{
            strokeDashoffset:
              STROKE_DASHARRAY - (strokeProgress / 100) * STROKE_DASHARRAY,
          }}
          d={`M${SVG_WIDTH / 2} 2 L${SVG_WIDTH - 26} 2 A${BORDER_RADIUS} ${BORDER_RADIUS} 0 0 1 ${SVG_WIDTH - 2} ${SVG_HEIGHT / 2} L${SVG_WIDTH - 2} ${SVG_HEIGHT / 2} A${BORDER_RADIUS} ${BORDER_RADIUS} 0 0 1 ${SVG_WIDTH - 26} ${SVG_HEIGHT - 2} L${BORDER_RADIUS + 2} ${SVG_HEIGHT - 2} A${BORDER_RADIUS} ${BORDER_RADIUS} 0 0 1 2 ${SVG_HEIGHT / 2} L2 ${SVG_HEIGHT / 2} A${BORDER_RADIUS} ${BORDER_RADIUS} 0 0 1 ${BORDER_RADIUS + 2} 2 L${SVG_WIDTH / 2} 2`}
          initial={false}
          stroke="#0786FF"
          strokeDasharray={STROKE_DASHARRAY}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          transition={{
            strokeDashoffset: {
              type: "spring",
              duration,
              bounce: 0,
            },
          }}
        />
      </svg>
    </motion.button>
  )
}

export default VoyageScrollIndicator
